import addProductHelpers from "./addProductHelpers";

/**
 * removes nested edges > node structure, to simplify accessing small queries
 * that don't need pageInfo, etc.
 * @param {Object} data - Shopify GraphQL Product query response
 * @returns
 */
export default function unwrapProducts(data) {
  if (!data) return [];

  const nodes = "edges" in data
    ? data.edges.map((edge) => edge?.node).filter(Boolean)
    : [data];

  return nodes.map((node) => {
    const unwrapKeys = ["variants", "media"];
    unwrapKeys.forEach((key) => {
      if (node[key]?.edges) {
        node[key] = node[key].edges.map(({ node }) => node);
      }
    });

    return node;
  });
}

/**
 * Merges a product group query result together with its Purple Dot
 * product family references into a single deduped list of products.
 * @param {Object} value - Shopify GraphQL Product/group query response, with an optional `family` metafield
 * @returns
 */
export function normalizeGroupValue(value) {
  if (!value) return [];

  const { family, ...productNode } = value;
  const familyProducts = family?.references
    ? unwrapProducts(family.references)
    : [];
  const selfProducts = productNode.id
    ? unwrapProducts(productNode)
    : unwrapProducts(value);

  const byId = new Map();
  [...familyProducts, ...selfProducts].forEach((item) => {
    if (item?.id) byId.set(item.id, item);
  });

  return [...byId.values()];
}

/**
 * Storefront search/product queries omit 0-inventory pre-order products,
 * so only require a handle for products that aren't published to the
 * online store yet.
 */
export function isVisibleStorefrontProduct(product) {
  return Boolean(product?.id && (product.onlineStoreUrl || product.handle));
}

export function hydrateGroupProducts(list) {
  return (list || [])
    .filter(isVisibleStorefrontProduct)
    .map((item) => (item.helpers ? item : addProductHelpers(item)));
}

export function mergeGroupProducts(...lists) {
  const seen = new Set();

  return lists.flat().filter((product) => {
    if (!product?.id || seen.has(product.id)) return false;
    seen.add(product.id);
    return true;
  });
}

/**
 * Reads the pre-rendered product family swatch data (fallback images/handles
 * for pre-order products the storefront API can't yet resolve) out of the
 * `[data-product-family-swatches]` script tag rendered by `product-form.liquid`.
 */
export function readFamilySwatchData(root) {
  const script =
    root?.querySelector?.("[data-product-family-swatches]") ||
    document.querySelector("[data-product-family-swatches]");

  if (!script) return [];

  try {
    const parsed = JSON.parse(script.textContent || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}
